import { useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import LabeledInput from 'modules/core/components/LabeledInput'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import type { ImportedEmployee } from 'modules/payroll_bundle/lib/sepaContext'

interface Props {
  employee?: ImportedEmployee | Employee
  index: number
  user: User | null | undefined
  noMatchFormat?: 'placeholder' | 'alert'
  type: string
  onAssignEmployee?: (userId: number, index: number) => void
  onUnAssignEmployee?: (userId: number, index: number) => void
}

const AssignEmployeeSelector = ({ user, index, onAssignEmployee, onUnAssignEmployee }: Props) => {
  const [open, setOpen] = useState(false)

  const employee =
    user && !user.isNew
      ? employees.find({
          access_id: user.attributes.id,
        })
      : undefined
  const employeeId = employee?.attributes.id
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(employeeId || null)

  const handleToggle = (isOpen: boolean) => {
    setOpen(isOpen)
  }

  const onSelectEmployee = (newEmployeeId: number | null | undefined) => {
    if (!newEmployeeId) {
      setSelectedEmployee(null)
    } else {
      if (selectedEmployee === newEmployeeId) {
        setSelectedEmployee(null)
        onUnAssignEmployee && onUnAssignEmployee(newEmployeeId, index)
      } else {
        setSelectedEmployee(newEmployeeId)
        onAssignEmployee && onAssignEmployee(newEmployeeId, index)
      }
    }

    handleToggle(false)
  }

  const SelectorInput = ({ value }: { value: number | null }) => {
    const placeholder = i18n.t('common.select_one')
    const valueLabel = (() => {
      if (!value) {
        return placeholder
      }

      const employee = employees.find({
        id: value,
      })
      const employeeName = employee ? users.get(employee.attributes.access_id)?.fullName() : ''

      return employeeName
    })()

    return (
      <Box
        paddingX="s16"
        paddingY="s8"
        flexDirection="row"
        height="s40"
        alignItems="center"
        width="full"
        cursor="pointer"
      >
        <Box flexDirection="row" width="full" gap="s6">
          <Box overflowX="hidden" textAlign="left">
            <Text size="300" color="grey1000" overflowWrap ellipsis capitalize>
              {valueLabel}
            </Text>
          </Box>
        </Box>
        <Icon.Small icon="caret-down" />
      </Box>
    )
  }

  return (
    <>
      <Box width="full">
        <PopoverMenu type={'list'} onClick={handleToggle} open={open}>
          <PopoverMenu.Trigger>
            <LabeledInput
              mandatory
              value={selectedEmployee ? 'value' : ''}
              isNotValid={selectedEmployee ? false : true}
            >
              <SelectorInput value={selectedEmployee} />
            </LabeledInput>
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="center" width="s400">
            <Box paddingY="s16" paddingX="s32">
              <EmployeeSelectorV2
                value={selectedEmployee ?? undefined}
                onChange={onSelectEmployee}
                options={{ asList: true }}
              />
            </Box>
          </PopoverMenu.Content>
        </PopoverMenu>
      </Box>
    </>
  )
}

export default AssignEmployeeSelector
